import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { resources } from "@/lib/resources";
import { FullScreenSpinner } from "@/components/ui/FullScreenSpinner";
import { AffiliateCompletionGate } from "@/components/affiliate/AffiliateCompletionGate";
import { homeFor } from "@shared/roleHome";

// The auth gate reuses the shared boot spinner, so waiting on getSession() looks
// identical to (and sits in the same spot as) the index.html loader and the
// Suspense fallback — one continuous spinner across the whole cold start.
const FullScreenLoader = FullScreenSpinner;


/** Requires a signed-in, email-verified SELLER. */
export function ProtectedRoute() {
  const { user, isVerified, isSeller, hasPurchased, isAffiliate, loading, capsLoading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isVerified) return <Navigate to="/verify-email" replace />;
  // Deciding on the pre-probe `false` would bounce every seller out of their own
  // dashboard on a hard refresh.
  if (capsLoading) return <FullScreenLoader />;
  if (!isSeller) return <Navigate to={homeFor({ isSeller: false, hasPurchased, isAffiliate })} replace />;
  return <Outlet />;
}

/**
 * Requires a signed-in, verified buyer. The buyer portal only exists once there
 * is something in it: an account that has never bought has no library, no orders
 * and no certificates, so it is sent somewhere it can actually act.
 */
export function CustomerRoute() {
  const { user, isVerified, isSeller, hasPurchased, isAffiliate, loading, capsLoading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  // The destination rides in the URL as well as in router state: on a storefront
  // host /account/login bounces to the apex via ApexRedirect, a full-page
  // navigation that drops router state. Without `?next=` a buyer sent here from
  // /access/:token logged in successfully and still landed on /account, never on
  // the product they had just paid for.
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/account/login?next=${encodeURIComponent(next)}`}
        replace
        state={{ from: location }}
      />
    );
  }
  if (!isVerified) return <Navigate to="/verify-email" replace />;
  if (capsLoading) return <FullScreenLoader />;
  if (!hasPurchased) return <Navigate to={homeFor({ isSeller, hasPurchased: false, isAffiliate })} replace />;
  return <Outlet />;
}

/**
 * Requires nothing more than a signed-in, email-verified account.
 *
 * The affiliate portal needs exactly this and no more. It used to sit behind
 * CustomerRoute, which also demands `hasPurchased` — so an open-tier affiliate,
 * someone who signed up to promote WITHOUT ever buying, was bounced out of their
 * own portal every time. That tier is a supported way to join the programme, so
 * gating its portal on a purchase made it unreachable by design.
 */
export function VerifiedRoute() {
  const { user, isVerified, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/account/login?next=${encodeURIComponent(next)}`}
        replace
        state={{ from: location }}
      />
    );
  }
  if (!isVerified) return <Navigate to="/verify-email" replace />;
  return <Outlet />;
}

/**
 * Requires an ACTIVE affiliate account — the gate on the portal itself.
 *
 * VerifiedRoute alone let anyone with a verified email walk into /affiliate/*:
 * the shell rendered in full, sidebar and all ten destinations, and only the
 * inner page 403'd — which reads as a broken portal, not a closed door. The gate
 * belongs here, on the door, rather than repeated inside ten pages.
 *
 * `pending` is turned away with everyone else. An open-tier application waiting
 * on an operator could otherwise read the terms and save a bank account, which
 * made "awaiting approval" mean nothing but a pause on commission.
 */
export function AffiliateRoute() {
  const { data, loading } = useFetch(() => resources.affiliates.me(), []);

  if (loading) return <FullScreenLoader />;
  // Anything but active — no account, pending, suspended, or a failed probe —
  // goes to /affiliate, which is the one screen that explains which of those it
  // is and what to do about it.
  if (data?.affiliate?.status !== "active") return <Navigate to="/affiliate" replace />;
  return (
    <AffiliateCompletionGate>
      <Outlet />
    </AffiliateCompletionGate>
  );
}

/** Requires a verified platform operator (`profiles.is_platform_admin`). */
export function AdminRoute() {
  const { user, isVerified, isPlatformAdmin, loading, capsLoading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isVerified) return <Navigate to="/verify-email" replace />;
  // Wait for the probe before deciding — otherwise a hard refresh of
  // /admin bounces the admin to /dashboard on the pre-probe `false`.
  if (capsLoading) return <FullScreenLoader />;
  if (!isPlatformAdmin) return <Navigate to="/dashboard/overview" replace />;
  return <Outlet />;
}

/** Requires a verified reseller (`profiles.is_reseller` + provisioned row). */
export function ResellerRoute() {
  const { user, isVerified, isReseller, loading, capsLoading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isVerified) return <Navigate to="/verify-email" replace />;
  // Wait for the probe before deciding — otherwise a hard refresh of
  // /reseller bounces the reseller to /dashboard on the pre-probe `false`.
  if (capsLoading) return <FullScreenLoader />;
  if (!isReseller) return <Navigate to="/dashboard/overview" replace />;
  return <Outlet />;
}

/**
 * For auth pages — redirects already-verified users to where they belong.
 * /register is deliberately no longer behind this: a signed-in buyer upgrading
 * to a seller must be able to reach that form, and bouncing them away is the
 * exact dead end this feature removes.
 */
export function PublicOnlyRoute() {
  const { user, isVerified, isSeller, hasPurchased, isAffiliate, loading, capsLoading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user && isVerified) {
    if (capsLoading) return <FullScreenLoader />;
    return <Navigate to={homeFor({ isSeller, hasPurchased, isAffiliate })} replace />;
  }
  return <Outlet />;
}
